import classNames from "classnames";
import React, { useContext } from "react";
import { SiteSettingsContext } from "../context/SiteSettingsContext";
import useTranslations from "../hooks/useTranslations";
import { LinkInfo } from "../models/shared/LinkInfo";
import { SanityImageSourceExt } from "../models/shared/SanityImageSourceExt";
import { isNullOrWhiteSpace, replaceWithUnicode } from "../shared/string";
import { ImagePresetCollection, validImage } from "../utilities/image";
import { formatPhoneNumberLink } from "../utilities/links";
import BackgroundBlockPattern, {
  BackgroundBlockPatternType
} from "./BackgroundBlockPattern";
import Button, { ButtonSize } from "./Buttons/Button";
import { Picture } from "./Picture";
import { renderLinkTitle } from "./SanityLink";

const imagePresets: ImagePresetCollection = {
  default: {
    height: 480,
    width: 360
  },
  sm: {
    height: 448,
    width: 1024
  },
  lg: {
    height: 544,
    width: 1440
  },
  "2xl": {
    height: 544,
    width: 2560
  }
};

export interface Props {
  backgroundPattern?: BackgroundBlockPatternType;
  ctaButton?: LinkInfo;
  image?: SanityImageSourceExt;
  makeRoomForHeader?: boolean;
  subtitle?: string;
  title?: string;
  hideSiteContact?: boolean;
  tags?: string[];
  additionalClassnames?: string;
}

const HeroWithOverlay = (props: Props) => {
  const {
    title,
    subtitle,
    image,
    backgroundPattern,
    ctaButton,
    makeRoomForHeader,
    hideSiteContact = false,
    tags = [],
    additionalClassnames = ""
  } = props;

  const siteSettings = useContext(SiteSettingsContext);

  const translations = useTranslations();

  return (
    <div
      className={classNames(
        "relative bg-hero mb-4 w-full min-h-hero-image-with-overlay-mobile",
        "lg:min-h-hero-image lg:slice-bottom lg:z-1 lg:mb-12",
        makeRoomForHeader ? "lg:pt-40" : "",
        additionalClassnames
      )}
    >
      {backgroundPattern && (
        <BackgroundBlockPattern
          {...backgroundPattern}
          zIndexClass="z-10"
          margins={{
            baseBlockMarginTop: "9rem"
          }}
        />
      )}
      {validImage(image) && (
        <div className="absolute z-0 inset-0">
          <Picture
            image={image}
            imgClassName="z-0 w-full h-full relative object-cover object-center"
            presets={imagePresets}
            imgRole="presentation"
          />
          {/* Overlay */}
          <div className="absolute z-10 bg-hero-overlay inset-0"></div>
        </div>
      )}
      <div
        className={classNames(
          "text-hero-with-overlay-text relative z-10 max-w-screen-xl w-full mx-auto px-4 py-8 lg:py-24",
          {
            "pt-[5.5rem] lg:pt-24": tags?.length > 0
          }
        )}
      >
        {tags && tags?.length > 0 && (
          <div className="mb-5">
            {tags.map((tag, index) => (
              <span
                key={`${tag}-${index}`}
                className="text-blue-800 bg-white px-4 py-2 rounded-full mr-2 text-[1rem] lg:text-[22px]"
              >
                {tag}
              </span>
            ))}
          </div>
        )}
        {!isNullOrWhiteSpace(title) && <h1>{replaceWithUnicode(title)}</h1>}

        {!isNullOrWhiteSpace(subtitle) && (
          <h2 className="text-hero-with-overlay-subtitle font-normal mt-4 lg:mt-0 text-2xl lg:text-4xl">
            {subtitle}
          </h2>
        )}

        <div className="flex flex-col items-center mt-12 lg:flex-row">
          {ctaButton && (
            <Button size={ButtonSize.Normal} color="secondary" type="link" {...ctaButton}>
              {renderLinkTitle(ctaButton)}
            </Button>
          )}
          {!!siteSettings?.siteContact?.phoneNumber?.length && !hideSiteContact && (
            <div className="flex flex-col justify-center mt-6 lg:mt-0 lg:ml-12 font-bold text-center">
              <div className="uppercase text-lg">{translations.orCall}</div>
              <div>
                <a
                  href={formatPhoneNumberLink(siteSettings.siteContact.phoneNumber)}
                  className="text-3xl text-current no-underline hover:opacity-80 hover:underline focus:outline-none focus:shadow-outline-neutral"
                >
                  {siteSettings.siteContact.phoneNumber}
                </a>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default HeroWithOverlay;

